import { useEffect, useState, useMemo, useContext } from 'react'
import { listUsers } from '../utils/queries'
import { selectData } from 'layouts/onboarding'
import { Link, useNavigate } from 'react-router-dom'
import { IntakeContext } from 'context/IntakeContext'
import { format, differenceInYears } from 'date-fns'
import {
  DataGrid,
  GridToolbar,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid'
import { Box, Button, CircularProgress } from '@mui/material'
import { GlobalStyles } from '@mui/system'
import { Refresh } from '@mui/icons-material'
import { toast } from 'react-toastify'
import { AccountAssumption } from 'admin/utils/AccountAssumption'

function DataGridStyles() {
  return (
    <GlobalStyles
      styles={{
        /* toolbar quick filter + filter panel (portal) */
        '.MuiDataGrid-toolbarQuickFilter .MuiInput-underline::before, .MuiDataGrid-panelContent .MuiInput-underline::before':
          {
            borderRadius: '8px',
          },

        '.MuiDataGrid-toolbarQuickFilter .MuiInput-underline:hover:not(.Mui-disabled)::before, .MuiDataGrid-panelContent .MuiInput-underline:hover:not(.Mui-disabled)::before':
          {
            borderBottomColor: '#5D729E',
          },

        '.MuiDataGrid-toolbarQuickFilter .MuiInput-underline::after, .MuiDataGrid-panelContent .MuiInput-underline::after':
          {
            borderBottomColor: '#2370ED',
            borderRadius: '8px',
            height: '0px',
            transition:
              'transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms, height 200ms cubic-bezier(0.0, 0, 0.2, 1) 100ms',
          },
        '.MuiDataGrid-toolbarQuickFilter .MuiInput-underline.Mui-focused::after, .MuiDataGrid-panelContent .MuiInput-underline.Mui-focused::after':
          {
            height: '20px',
          },
        '.MuiDataGrid-toolbarContainer > div > button, .MuiDataGrid-toolbarContainer > div > button:hover, .MuiDataGrid-toolbarContainer > div > button:focus:not(:hover), .MuiDataGrid-panelContent label.Mui-focused':
          {
            color: '#2370ED',
          },
      }}
    />
  )
}

export const UsersTable = () => {
  const [rows, setRows] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({})
  const { assumeUserAccount, intakeState } = useContext(IntakeContext)
  const navigate = useNavigate()

  const dollarFormat = Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })

  const essentialFields = [
    'firstName',
    'lastName',
    'email',
    'phone',
    'agency',
    'retirementSystem',
  ]
  const orderedKeys = [
    'retirementStrategist',
    'parcoScore',
    'retirementIncomeGap',
    'totalInvestableAssets',
    'age',
    'firstName',
    'lastName',
    'fullName',
    'email',
    'phone',
    'address',
    'address2',
    'city',
    'state',
    'zipcode',
    'governmentEmployee',
    'agency',
    'retirementSystem',
    'dateOfBirth',
    'employmentStatus',
    'dateOfHire',
    'dateOfPlannedRetirement',
    'baseSalary',
    'highThree',
    'netTakeHome',
    'currentLeaveBalance',
    'currentSickLeaveBalance',
    'hasWill',
    'hasTrust',
    'military',
    'militaryBranch',
    'familyMaritalStatus',
    'giftCard',
    'lastLoginDate',
    'createdAt',
    'updatedAt',
  ]

  const assumeIntoUser = async (userId) => {
    await assumeUserAccount(userId)
    window.location.href = '/admin/admin-inputs'
  }

  // const CustomToolbar = ({ onRefresh, ...props }) => {
  //   return (
  //     // <Box
  //     //   sx={{
  //     //     display: 'flex',
  //     //     justifyContent: 'space-between',
  //     //     alignItems: 'center',
  //     //   }}
  //     // >
  //     <>
  //       <GridToolbar {...props} />
  //       <Button
  //         variant="parcoRounded"
  //         size="small"
  //         startIcon={<Refresh />}
  //         onClick={onRefresh}
  //       >
  //         Refresh
  //       </Button>
  //     </>
  //     // </Box>
  //   )
  // }

  const CustomToolbar = ({ onRefresh }) => {
    return (
      <GridToolbarContainer
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          <GridToolbarExport csvOptions={{ fileName: 'PARCO-users-export' }} />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="parcoRounded"
            startIcon={
              isLoading ? (
                <CircularProgress
                  size={'1em'}
                  sx={{ color: '#fff !important' }}
                />
              ) : (
                <Refresh />
              )
            }
            onClick={onRefresh}
            sx={{ color: '#fff !important', height: '100%' }}
          >
            {isLoading ? 'Refreshing List...' : 'Refresh List'}
          </Button>
          <GridToolbarQuickFilter debounceMs={300} />
        </Box>
      </GridToolbarContainer>
    )
  }

  const renderFormattedField = ({ options, noneLabel = 'None Selected' }) => {
    const formatter = (params) => {
      const value = params?.value
      const label = value
        ? options.find((opt) => opt.value === value)?.label || value
        : noneLabel

      const isPlaceholder = !value || label === noneLabel

      return (
        <span
          style={isPlaceholder ? { fontStyle: 'italic', color: '#ddd' } : {}}
        >
          {label}
        </span>
      )
    }
    formatter.displayName = 'RenderFormattedFieldCell'
    return formatter
  }

  const renderNullableField = (formatFn = null, noneLabel = 'Empty') => {
    const renderer = (params) => {
      const value = params?.value
      const isMissing = value === undefined || value === null || value === ''

      const displayValue = isMissing
        ? noneLabel
        : formatFn
        ? formatFn(value)
        : value

      return (
        <span style={isMissing ? { fontStyle: 'italic', color: '#ddd' } : {}}>
          {displayValue}
        </span>
      )
    }
    renderer.displayName = 'RenderNullableFieldCell'
    return renderer
  }

  const formatDate = (value) =>
    value ? format(new Date(value), 'MM/dd/yyyy') : undefined

  const formatCurrency = (value) =>
    typeof value === 'number' ? dollarFormat.format(value) : value

  const formatFloat = (value) =>
    typeof value === 'number' ? value.toFixed(2) : value

  const formatPercentage = (value) =>
    typeof value === 'number' ? `${value.toFixed(2)}%` : `${value}%`

  const formatBoolean = (value) =>
    value === true ? 'Yes' : value === false ? 'No' : ''

  const fieldRenderConfig = {
    agency: {
      type: 'select',
      options: selectData.agency,
    },
    state: {
      type: 'select',
      options: selectData.states,
    },
    retirementSystem: {
      type: 'select',
      options: selectData.retirementSystem,
    },
    phone: {
      type: 'formatted',
      formatter: (value) => {
        const digits = String(value).replace(/\D/g, '')
        return digits.length === 10
          ? `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
          : value
      },
      noneLabel: '—',
    },
    dateOfBirth: {
      type: 'formatted',
      formatter: formatDate,
      noneLabel: '—',
    },
    dateOfHire: {
      type: 'formatted',
      formatter: formatDate,
      noneLabel: '—',
    },
    dateOfPlannedRetirement: {
      type: 'formatted',
      formatter: formatDate,
      noneLabel: '—',
    },
    baseSalary: {
      type: 'formatted',
      formatter: formatCurrency,
      noneLabel: '—',
    },
    highThree: {
      type: 'formatted',
      formatter: formatCurrency,
      noneLabel: '—',
    },
    netTakeHome: {
      type: 'formatted',
      formatter: formatCurrency,
      noneLabel: '—',
    },
    retirementIncomeGap: {
      type: 'formatted',
      formatter: formatCurrency,
      noneLabel: '—',
    },
    totalInvestableAssets: {
      type: 'formatted',
      formatter: formatCurrency,
      noneLabel: '—',
    },
    tspBalance: {
      type: 'formatted',
      formatter: formatCurrency,
      noneLabel: '—',
    },
    tspRothBalance: {
      type: 'formatted',
      formatter: formatCurrency,
      noneLabel: '—',
    },
    estimatedSocialSecurity: {
      type: 'formatted',
      formatter: formatCurrency,
      noneLabel: '—',
    },
    healthInsurancePremium: {
      type: 'formatted',
      formatter: formatCurrency,
      noneLabel: '—',
    },
    currentLeaveBalance: {
      type: 'formatted',
      formatter: formatFloat,
      noneLabel: '—',
    },
    currentSickLeaveBalance: {
      type: 'formatted',
      formatter: formatFloat,
      noneLabel: '—',
    },
    parcoScore: {
      type: 'formatted',
      formatter: formatFloat,
      noneLabel: '—',
    },
    tspContribution: {
      type: 'formatted',
      formatter: formatPercentage,
      noneLabel: '—',
    },
    tspRothContribution: {
      type: 'formatted',
      formatter: formatPercentage,
      noneLabel: '—',
    },
    hasWill: {
      type: 'formatted',
      formatter: formatBoolean,
      noneLabel: '—',
    },
    hasTrust: {
      type: 'formatted',
      formatter: formatBoolean,
      noneLabel: '—',
    },
    governmentEmployee: {
      type: 'formatted',
      formatter: formatBoolean,
      noneLabel: '—',
    },
    tsp: {
      type: 'formatted',
      formatter: formatBoolean,
      noneLabel: '—',
    },
    tspRoth: {
      type: 'formatted',
      formatter: formatBoolean,
      noneLabel: '—',
    },
    tspLoans: {
      type: 'formatted',
      formatter: formatBoolean,
      noneLabel: '—',
    },
    lifeInsuranceOther: {
      type: 'formatted',
      formatter: formatBoolean,
      noneLabel: '—',
    },
    fegli: {
      type: 'formatted',
      formatter: formatBoolean,
      noneLabel: '—',
    },
    military: {
      type: 'formatted',
      formatter: formatBoolean,
      noneLabel: '—',
    },
    militaryBranch: {
      type: 'select',
      options: selectData.militaryBranch,
    },
    retirementStrategist: {
      type: 'select',
      options: selectData.retirementStrategist,
    },
    createdAt: {
      type: 'formatted',
      formatter: formatDate,
      noneLabel: '—',
    },
    updatedAt: {
      type: 'formatted',
      formatter: formatDate,
      noneLabel: '—',
    },
  }

  useEffect(() => {
    const fetchAndSummarize = async () => {
      setIsLoading(true)

      const applyData = (users) => {
        setRows(users)

        // Collect all unique keys from the dataset
        const allKeys = Array.from(
          new Set(users.flatMap((u) => Object.keys(u)))
        )

        // Load user’s saved visibility preferences if they exist
        const saved = localStorage.getItem('columnVisibility')
        if (saved) {
          setColumnVisibilityModel(JSON.parse(saved))
        } else {
          const defaultHidden = allKeys.reduce((acc, key) => {
            if (!essentialFields.includes(key) || key === 'id') {
              acc[key] = false
            }
            return acc
          }, {})
          setColumnVisibilityModel(defaultHidden)
        }
      }

      // check for cached copy in localStorage
      // const cached = localStorage.getItem('usersList')
      // if (cached) {
      //   // console.log('Loaded users from cache')
      //   applyData(JSON.parse(cached))
      //   setIsLoading(false)
      //   return
      // }

      // otherwise fetch fresh
      const allUsers = await listUsers()
      const normalized = allUsers.map((u, i) => ({
        id: u.id || i,
        ...u,
        age: u.dateOfBirth
          ? differenceInYears(new Date(), new Date(u.dateOfBirth))
          : '—',
        lastLoginDate: u.updatedAt
          ? format(new Date(u.updatedAt), 'MM/dd/yyyy h:mm a')
          : 'Empty',
      }))

      localStorage.setItem('usersList', JSON.stringify(normalized))
      applyData(normalized)
      setIsLoading(false)
    }

    fetchAndSummarize()
  }, [])

  const handleColumnVisibilityChange = (newModel) => {
    // console.log('Saving visibility:', newModel)
    setColumnVisibilityModel(newModel)
    localStorage.setItem('columnVisibility', JSON.stringify(newModel))
  }

  const allColumns = useMemo(() => {
    if (rows.length === 0) return []

    // add in all keys without ID included
    const allKeys = Array.from(
      new Set(rows.flatMap((row) => Object.keys(row)))
    ).filter((key) => key !== 'id')

    const finalKeyOrder = [
      ...orderedKeys,
      ...allKeys.filter((k) => !orderedKeys.includes(k)),
    ]
    const base = finalKeyOrder.map((key) => {
      let column = {
        field: key,
        headerName: key
          .replace(/([a-z])([A-Z])/g, '$1 $2')
          .replace(/^./, (s) => s.toUpperCase()),
        flex: key === 'email' || key === 'agency' ? 2 : 1,
        minWidth: 120,
      }

      const config = fieldRenderConfig[key]
      if (config) {
        switch (config.type) {
          case 'select':
            column.renderCell = renderFormattedField({
              options: config.options,
              noneLabel: config.noneLabel || 'None Selected',
            })
            break
          case 'formatted':
            column.renderCell = renderNullableField(
              config.formatter,
              config.noneLabel || 'None Selected'
            )
            break
          default:
            break
        }
      } else {
        // Fallback: for any other key, apply default nullable styling
        column.renderCell = renderNullableField()
      }

      return column
    })

    return [
      ...base,
      {
        field: 'loginAs',
        headerName: 'Login As',
        sortable: false,
        filterable: false,
        disableColumnMenu: false,
        flex: 1,
        minWidth: 160,
        renderCell: (params) => {
          const user = params.row
          return (
            <Button
              variant="parcoRounded"
              size="small"
              onClick={() =>
                assumeIntoUser(user.id, user.firstName, user.lastName)
              }
            >
              Login as {user.firstName ? user.firstName : 'No Name'}
            </Button>
          )
        },
      },
    ]
  }, [rows])

  return (
    <Box
      sx={{
        height: '90vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <DataGridStyles />
      <DataGrid
        rows={rows}
        columns={allColumns}
        loading={isLoading}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        disableRowSelectionOnClick
        pagination
        slots={{
          toolbar: CustomToolbar, // Add the search bar
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: { fileName: 'PARCO-users-export' },
            quickFilterProps: { debounceMs: 300 },
            onRefresh: async () => {
              // Clear cache and re-fetch
              localStorage.removeItem('usersList')
              setIsLoading(true)

              const allUsers = await listUsers()
              const normalized = allUsers.map((u, i) => ({
                id: u.id || i,
                ...u,
                age: u.dateOfBirth
                  ? differenceInYears(new Date(), new Date(u.dateOfBirth))
                  : '—',
                lastLoginDate: u.updatedAt
                  ? format(new Date(u.updatedAt), 'MM/dd/yyyy h:mm a')
                  : 'Empty',
              }))

              localStorage.setItem('usersList', JSON.stringify(normalized))
              setRows(normalized)
              setIsLoading(false)
            },
          },
        }}
        sx={{
          '&.MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-columnHeaders': {
            position: 'sticky',
            top: 0,
            backgroundColor: 'white',
            zIndex: 1,
          },
          '& .MuiTablePagination-root': {
            overflow: 'unset',
          },
          '& .MuiTablePagination-toolbar .MuiInputBase-root': {
            width: 'auto !important',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          '& .MuiCircularProgress-root': {
            color: '#2370ED',
          },
        }}
      />
    </Box>
  )
}

// THIS VERSION IS FOR JOHN

// import { useEffect, useState } from 'react'
// import { API, graphqlOperation } from 'aws-amplify'
// import { listParcoUsers } from 'graphql/custom/queries'

// import {
//   format,
//   differenceInYears,
//   subYears,
//   addYears,
//   addDays,
//   addMonths,
//   max,
//   min,
//   differenceInMonths,
//   subMonths,
// } from 'date-fns'

// import DataTable from 'components/DataTable'

// export const UsersTable = () => {
//   const [rows, setRows] = useState([])
//   const [isLoading, setIsLoading] = useState(false)

//   useEffect(() => {
//     const fetchAndSummarize = async () => {
//       setIsLoading(true)

//       // 1) page through every ParcoUser
//       let allUsers = []
//       let nextToken = null
//       do {
//         const resp = await API.graphql(
//           graphqlOperation(listParcoUsers, { limit: 100, nextToken })
//         )
//         const { items, nextToken: newToken } = resp.data.listParcoUsers
//         allUsers = allUsers.concat(items)
//         nextToken = newToken
//       } while (nextToken)

//       // 2) map down to only the fields you asked for
//       const slim = allUsers.map((u) => ({
//         firstName: u.firstName || 'Empty',
//         lastName: u.lastName || 'Empty',
//         phone: u.phone || 'Empty',
//         state: u.state || 'Empty',
//         age: u.dateOfBirth
//           ? differenceInYears(new Date(), new Date(u.dateOfBirth))
//           : 'Unknown',
//         investableAssets:
//           u.totalInvestableAssets != null ? u.totalInvestableAssets : 'N/A',
//         salesforceAccountID: u.salesforceAccountID || 'Empty',
//         salesforceEventID: u.salesforceICEventID || 'Empty',
//         salesforceLeadID: u.salesforceLeadID || 'Empty',
//         tspTradBalance: u.tspBalance != null ? u.tspBalance : 'N/A',
//         lastLoginDate: u.updatedAt
//           ? format(new Date(u.updatedAt), 'MM/dd/yyyy h:mm a')
//           : 'Empty',
//       }))

//       setRows(slim)
//       console.log('user summaries:', slim)
//       setIsLoading(false)
//     }

//     fetchAndSummarize()
//   }, [])

//   const columns = [
//     { Header: 'First Name', accessor: 'firstName' },
//     { Header: 'Last Name', accessor: 'lastName' },
//     { Header: 'Phone', accessor: 'phone' },
//     { Header: 'State', accessor: 'state' },
//     { Header: 'Age', accessor: 'age' },
//     { Header: 'TSP Trad Balance', accessor: 'tspTradBalance' },
//     { Header: 'Investable Assets', accessor: 'investableAssets' },
//     { Header: 'Last Login Date', accessor: 'lastLoginDate' },

//     { Header: 'SF Account ID', accessor: 'salesforceAccountID' },
//     { Header: 'SF Event ID', accessor: 'salesforceEventID' },
//     { Header: 'SF Lead ID', accessor: 'salesforceLeadID' },
//   ]

//   return (
//     <div>
//       {isLoading ? (
//         <p>Loading…</p>
//       ) : (
//         <DataTable
//           table={{ columns, rows }}
//           canSearch
//           entriesPerPage={{ defaultValue: 2000 }}
//           isLoading={isLoading}
//         />
//       )}
//     </div>
//   )
// }

// import React, { useEffect, useMemo, useState } from 'react'
// import { API, graphqlOperation } from 'aws-amplify'
// import { listParcoUsers } from 'graphql/queries'
// import { format, differenceInYears } from 'date-fns'
// import { startCase } from 'lodash'
// import {
//   Checkbox,
//   IconButton,
//   Menu,
//   MenuItem,
//   Tooltip,
//   Typography,
// } from '@mui/material'
// import SettingsIcon from '@mui/icons-material/Settings'

// // Replace with your table component
// import DataTable from 'components/DataTable'

// export const UsersTable = () => {
//   const [rows, setRows] = useState([])
//   const [isLoading, setIsLoading] = useState(false)

//   const [anchorEl, setAnchorEl] = useState(null)
//   const [hiddenColumns, setHiddenColumns] = useState([])

//   const open = Boolean(anchorEl)
//   const handleMenuOpen = () => {
//     setAnchorEl(event.currentTarget)
//   }
//   const handleMenuClose = () => {
//     setAnchorEl(null)
//   }

//   useEffect(() => {
//     const fetchAndSummarize = async () => {
//       setIsLoading(true)
//       let allUsers = []
//       let nextToken = null

//       do {
//         const resp = await API.graphql(
//           graphqlOperation(listParcoUsers, { limit: 100, nextToken })
//         )
//         const { items, nextToken: newToken } = resp.data.listParcoUsers
//         allUsers = allUsers.concat(items.filter(Boolean))

//         nextToken = newToken
//       } while (nextToken)

//       const normalized = allUsers.map((u) => {
//         const flat = {
//           ...u,
//           firstName: u.firstName || 'Empty',
//           lastName: u.lastName || 'Empty',
//           phone: u.phone || 'Empty',
//           state: u.state || 'Empty',
//           age: u.dateOfBirth
//             ? differenceInYears(new Date(), new Date(u.dateOfBirth))
//             : 'Unknown',
//           investableAssets:
//             u.totalInvestableAssets != null ? u.totalInvestableAssets : 'N/A',
//           tspTradBalance: u.tspBalance ?? 'N/A',
//           lastLoginDate: u.updatedAt
//             ? format(new Date(u.updatedAt), 'MM/dd/yyyy h:mm a')
//             : 'Empty',
//         }
//         return flat
//       })

//       console.log(allUsers)

//       setRows(normalized)
//       setIsLoading(false)
//     }

//     fetchAndSummarize()
//   }, [])

//   const allColumns = useMemo(() => {
//     if (rows.length === 0) return []

//     return Object.keys(rows[0]).map((key) => ({
//       Header: startCase(key),
//       accessor: key,
//     }))
//   }, [rows])

//   const visibleColumns = useMemo(() => {
//     return allColumns.filter((col) => !hiddenColumns.includes(col.accessor))
//   }, [allColumns, hiddenColumns])

//   const toggleColumn = (columnId) => {
//     setHiddenColumns((prev) =>
//       prev.includes(columnId)
//         ? prev.filter((col) => col !== columnId)
//         : [...prev, columnId]
//     )
//   }

//   // Hide all but the most essential by default
//   useEffect(() => {
//     if (rows.length > 0) {
//       const defaults = Object.keys(rows[0]).filter(
//         (key) =>
//           !['firstName', 'lastName', 'phone', 'state', 'age'].includes(key)
//       )
//       setHiddenColumns(defaults)
//     }
//   }, [rows])

//   return (
//     <div>
//       <div style={{ display: 'flex', justifyContent: 'space-between' }}>
//         <Typography variant="h6" gutterBottom>
//           Users Table
//         </Typography>
//         <Tooltip title="Select columns">
//           <IconButton onClick={handleMenuOpen}>
//             <SettingsIcon />
//           </IconButton>
//         </Tooltip>
//         <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
//           {allColumns.map((col) => (
//             <MenuItem key={col.accessor}>
//               <Checkbox
//                 checked={!hiddenColumns.includes(col.accessor)}
//                 onChange={() => toggleColumn(col.accessor)}
//               />
//               {col.Header}
//             </MenuItem>
//           ))}
//         </Menu>
//       </div>

//       <DataTable
//         table={{ columns: visibleColumns, rows }}
//         canSearch
//         entriesPerPage={{ defaultValue: 10 }}
//         isLoading={isLoading}
//       />
//     </div>
//   )
// }
